import { useEffect, useState, type ReactNode } from 'react';
import { apiClient } from '@indipia/shared';
import { EmptyState, ErrorState, IconCheckCircle, IconInfoCircle, IconWarningCircle } from '@indipia/ui-core';

type RespondAction = 'CONFIRMED' | 'UNCONFIRMED';

// 회신 화면 표시 정보 — 현장명·공종·작업기간은 API가 표시용 조회에 실패하면 비워 보낸다(없으면 해당 줄만 생략).
interface RespondInfo {
  siteName?: string | null;
  workTypeName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

type RespondState =
  | { status: 'loading' }
  | { status: 'done'; action: RespondAction; info: RespondInfo }
  | { status: 'already'; action: RespondAction | null; info: RespondInfo }
  | { status: 'closed' }
  | { status: 'deadline' }
  | { status: 'error' };

// 마감 화면으로 통합하는 응답 — 사유(앞당김 마감·반장 교체)는 API가 구분해 주지 않으므로 단일 화면.
// 답장 기한이 지난 열린 요청(409 DEADLINE_PASSED)만 별도 'deadline' 상태로 분리해 전용 문구를 보여준다.
//  · Planner 409 REQUEST_CLOSED (반장 불일치·마감·옛 링크·회신 대기 아님), 404 TASK_NOT_FOUND (삭제된 공종)
//  · ShareGate TOKEN_REVOKED/TOKEN_EXPIRED (410) — 정상 경로에선 /v/{token} 진입 단계에서 302 /share-error 로 차단되어
//    이 API까지 오지 않지만, 방어적으로 같은 화면으로 처리한다.
const CLOSED_CODES = new Set(['REQUEST_CLOSED', 'TASK_NOT_FOUND', 'TOKEN_REVOKED', 'TOKEN_EXPIRED']);

// 시공반장 배정 알림톡의 "일정가능"/"일정불가능" 버튼이 도착하는 공유 랜딩 페이지 (F-04 §7).
// 어느 버튼을 눌렀는지는 URL/쿼리에 담기지 않는다 — 버튼마다 서로 다른 공유 링크(resourceRef가
// sched:{taskId}:confirmed / unconfirmed로 갈리는 별도 shareId)로 발급되어 있어서, 이 페이지는
// 그냥 GET /schedule-shares/me/respond를 호출하고 응답의 action 값으로 어느 쪽인지 판단한다
// (X-Share-Scope 헤더는 FlowGate가 공유 쿠키를 검증해 자동으로 붙여준다 — 이 페이지는 신경쓰지 않음).
export function ForemanConfirmRespondPage() {
  const [state, setState] = useState<RespondState>({ status: 'loading' });

  useEffect(() => {
    apiClient
      .get<{ data: { action: RespondAction } & RespondInfo }>('/schedule/schedule-shares/me/respond')
      .then((res) => setState({ status: 'done', action: res.data.data.action, info: res.data.data }))
      .catch((err) => {
        const code = err?.response?.data?.code;
        const httpStatus = err?.response?.status;
        if (code === 'ALREADY_RESPONDED') {
          // 이미 회신 응답의 data 에 기존 회신 action·표시 필드가 실려 온다(없으면 보낸 내용 표시 생략).
          const data = err?.response?.data?.data as ({ action?: RespondAction } & RespondInfo) | null | undefined;
          const action = data?.action === 'CONFIRMED' || data?.action === 'UNCONFIRMED' ? data.action : null;
          setState({ status: 'already', action, info: data ?? {} });
        } else if (code === 'DEADLINE_PASSED') setState({ status: 'deadline' });
        else if (CLOSED_CODES.has(code) || httpStatus === 410) setState({ status: 'closed' });
        else setState({ status: 'error' });
      });
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="flex h-screen w-full items-center justify-center text-sm text-text-muted">
        처리 중…
      </div>
    );
  }

  if (state.status === 'error') {
    // 재시도 버튼 없음 — 이 GET 은 회신을 기록하는 호출이라 재호출 시 첫 호출 성공분이 '이미 답장'으로 보일 수 있다.
    return (
      <StateShell>
        <ErrorState
          title="응답을 처리하지 못했어요"
          description="잠시 후 카톡의 버튼을 다시 눌러 주세요. 계속 안 되면 요청한 분께 연락해 주세요."
        />
      </StateShell>
    );
  }

  if (state.status === 'closed') {
    return (
      <StateShell>
        <EmptyState
          icon={<IconInfoCircle />}
          title="이미 마감된 요청이에요"
          description="이번 일정은 다른 반장님과 먼저 진행하게 됐거나 답장 기한이 지난 요청이에요. 새 요청이 오면 그 카톡의 버튼을 눌러 주세요."
        />
      </StateShell>
    );
  }

  if (state.status === 'deadline') {
    return (
      <StateShell>
        <EmptyState
          icon={<IconInfoCircle />}
          title="마감된 일정이에요"
          description="답장 기한이 지났어요. 새 요청이 오면 그 카톡의 버튼을 눌러 주세요."
        />
      </StateShell>
    );
  }

  if (state.status === 'already') {
    return (
      <StateShell>
        <div className="flex flex-col items-center gap-3 text-center">
          <EmptyState
            icon={<IconCheckCircle />}
            title="이미 답장하셨어요"
            description="답장은 바꿀 수 없어요. 일정에 변동이 있으면 요청한 분께 연락해 주세요."
          />
          <ReplySummary action={state.action} info={state.info} />
        </div>
      </StateShell>
    );
  }

  const confirmed = state.action === 'CONFIRMED';
  return (
    <StateShell>
      <div className="flex flex-col items-center gap-3 text-center">
        <EmptyState
          icon={confirmed ? <IconCheckCircle /> : <IconWarningCircle />}
          title={confirmed ? '일정가능 회신을 보냈어요' : '일정불가능 회신을 보냈어요'}
        />
        <ReplySummary action={null} info={state.info} />
      </div>
    </StateShell>
  );
}

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

// 'YYYY-MM-DD'(또는 T 시각 포함) → 'M/D(요일)' — 시간대 변환 없이 문자열 기준.
function mdWeekday(v?: string | null): string {
  const m = v ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
  if (!m) return '';
  const wd = WEEKDAY[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()];
  return `${Number(m[2])}/${Number(m[3])}(${wd})`;
}

// 현장명 + '공종 · M/D(요일) ~ M/D(요일)' 읽기 전용 표시. action 이 있으면(이미 회신) 보낸 내용을 맨 위에 보여준다.
function ReplySummary({ action, info }: { action: RespondAction | null; info: RespondInfo }) {
  const start = mdWeekday(info.startDate);
  const end = mdWeekday(info.endDate);
  const period = start && end ? (start === end ? start : `${start} ~ ${end}`) : start || end;
  const detail = [info.workTypeName, period].filter(Boolean).join(' · ');
  const sent = action === 'CONFIRMED' ? '일정가능' : action === 'UNCONFIRMED' ? '일정불가능' : '';
  if (!sent && !info.siteName && !detail) return null;
  return (
    <div className="flex max-w-xs flex-col gap-1 text-sm text-text-secondary">
      {sent && <p className="font-bold text-text-primary">보낸 내용 · {sent}</p>}
      {info.siteName && <p>{info.siteName}</p>}
      {detail && <p>{detail}</p>}
    </div>
  );
}

// 회신 완료·마감·이미 답장 화면 공통 레이아웃 — 전체화면 중앙 정렬(D-07 §14.2: EmptyState 는 배경·보더가 없어 감싸는 쪽이 면을 가진다)
function StateShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-surface p-4">
      {children}
    </div>
  );
}
